import { useCallback, useState } from 'react';
import { noop } from 'underscore';
import { ArgsOf } from 'types';
import {
  ResizeCallback,
  ResizingHandle,
  TrackAssetProps,
  TrackAssetResizeStopCallback,
} from '../TrackAsset';

const DEFAULT_POSITION = { x: 0, y: 0 };

export type ClippableResizeCallback = (
  playFromOffset: number,
  ...args: ArgsOf<ResizeCallback>
) => void;

export type ClippableResizeStopCallback = (
  playFromOffset: number,
  resized: boolean,
  ...args: ArgsOf<ResizeCallback>
) => void;

export type UseClippableTrackAssetProps = Omit<
  TrackAssetProps,
  'sizeBounds' | 'onResize' | 'onResizeStart' | 'onResizeStop'
> & {
  fullDurationWidth: number;
  loopable?: boolean;
  onResize?: ClippableResizeCallback;
  onResizeStart?: ClippableResizeCallback;
  onResizeStop?: ClippableResizeStopCallback;
  playFromOffset: number;
  minWidth: number;
};

export default function useClippableTrackAsset(
  props: UseClippableTrackAssetProps,
) {
  const {
    fullDurationWidth,
    loopable = false,
    minWidth,
    onResize = noop,
    onResizeStart = noop,
    onResizeStop = noop,
    playFromOffset,
    position = DEFAULT_POSITION,
    width,
    ...restTrackAssetProps
  } = props;

  const [maxWidth, setMaxWidth] = useState<number | undefined>(undefined);

  const calculateMaxLoops = useCallback(() => {
    return !loopable ? 1 : (width + playFromOffset) / fullDurationWidth;
  }, [fullDurationWidth, loopable, playFromOffset, width]);

  const calculateMaxWidth = useCallback(
    (handle: ResizingHandle) => {
      const startPosition = position.x;
      const endPosition = startPosition + width;

      const playDurationWidth = endPosition - startPosition;

      if (loopable) {
        const val =
          handle === 'left'
            ? fullDurationWidth * calculateMaxLoops()
            : Infinity;
        return val;
      }

      const leftTrimOffset = playFromOffset;
      const rightTrimOffset =
        fullDurationWidth - (playFromOffset + playDurationWidth);
      const trimOffset = handle === 'left' ? leftTrimOffset : rightTrimOffset;

      return playDurationWidth + trimOffset;
    },
    [
      calculateMaxLoops,
      fullDurationWidth,
      loopable,
      playFromOffset,
      position.x,
      width,
    ],
  );

  const handleResizeStart: ResizeCallback = useCallback(
    (newWidth, handle, pos) => {
      setMaxWidth(calculateMaxWidth(handle));
      onResizeStart(playFromOffset, newWidth, handle, pos);
    },
    [calculateMaxWidth, onResizeStart, playFromOffset],
  );

  const handleResizeStop: TrackAssetResizeStopCallback = useCallback(
    (resized, newWidth, handle, pos) => {
      setMaxWidth(calculateMaxWidth(handle));
      onResizeStop(playFromOffset, resized, newWidth, handle, pos);
    },
    [calculateMaxWidth, onResizeStop, playFromOffset],
  );

  const handleResize: ResizeCallback = useCallback(
    (newWidth, handle, pos) => {
      const positionDelta = position.x - pos;
      const newPlayFromOffset =
        handle === 'left' ? playFromOffset - positionDelta : playFromOffset;

      /*
       * NB: newPlayOffset % (newPlayOffset + 1) === newPlayOffset
       * just hack so we can unconditionally calculate the mod below
       */
      const divisor = loopable ? fullDurationWidth : newPlayFromOffset + 1;

      onResize(newPlayFromOffset % divisor, newWidth, handle, pos);
    },
    [fullDurationWidth, loopable, onResize, playFromOffset, position.x],
  );

  return {
    maxWidth,
    handleResize,
    handleResizeStart,
    handleResizeStop,
    trackAssetProps: {
      position,
      width,
      ...restTrackAssetProps,
      onResize: handleResize,
      onResizeStart: handleResizeStart,
      onResizeStop: handleResizeStop,
      sizeBounds: {
        maxWidth,
        minWidth,
      },
    } as TrackAssetProps,
  };
}
